import { useMemo } from 'react';
import { useSquad } from '../lib/squadContext.ts';
import {
  complianceLevel,
  decimal,
  FOREIGN_LIMIT,
  isPlannedExit,
  LIST_A_LIMIT,
  money,
  ruleLevels,
} from '../lib/rules.ts';
import { resetPlan } from '../lib/store.ts';
import { C, HEADER } from '../lib/theme.ts';
import type { RuleLevel } from '../types.ts';

const COMPLIANCE_LABEL: Record<RuleLevel, string> = {
  ok: 'TEMİZ',
  warn: 'RİSKLİ',
  violation: 'İHLAL',
};

/** On the dark bar: white reads as fine, gold as a caution, red as an actual breach. */
const COMPLIANCE_COLOR: Record<RuleLevel, string> = {
  ok: HEADER.fg,
  warn: C.gold,
  violation: C.red,
};

/** Dark top bar: brand, season, KPI row, plus the status headline beneath it. */
export default function Header() {
  const { state, owned, listRules, squadRules } = useSquad();
  const wageCap = state.wageCap;

  // KPIs read the whole owned squad (loans/sales completed are already excluded),
  // except the two quota figures, which only count the first-team list.
  const kpi = useMemo(() => {
    const planned = owned.filter(isPlannedExit);
    const plannedValue = planned.reduce((total, p) => total + p.marketValue, 0);
    const plannedWage = planned.reduce((total, p) => total + p.wage, 0);
    // Same levels the rule cards use, so the headline can never contradict them.
    const compliance = complianceLevel(ruleLevels(listRules, squadRules, wageCap));

    return {
      count: owned.length,
      averageAge: decimal(squadRules.averageAge || 0, 1),
      value: money(squadRules.totalValue),
      wage: money(squadRules.totalWage),
      valueNote: planned.length
        ? 'planlı çıkış sonrası ' + money(squadRules.totalValue - plannedValue)
        : 'planlı çıkış yok',
      wageNote: planned.length
        ? 'planlı çıkış sonrası ' + money(squadRules.totalWage - plannedWage)
        : money(wageCap) + ' bütçe',
      wageFg: squadRules.totalWage > wageCap ? C.gold : HEADER.fg,
      foreign: listRules.foreign.length + '/' + FOREIGN_LIMIT,
      foreignFg: listRules.tffViolation ? C.gold : HEADER.fg,
      rating: decimal(
        owned.reduce((total, p) => total + p.rating, 0) / (owned.length || 1),
        2,
      ),
      compliance: COMPLIANCE_LABEL[compliance],
      complianceFg: COMPLIANCE_COLOR[compliance],
    };
  }, [owned, listRules, squadRules, wageCap]);

  const headline = useMemo(() => {
    if (listRules.foreign.length > FOREIGN_LIMIT) {
      return (
        'A Takım Listesi\'nde ' +
        listRules.foreign.length +
        ' yabancı var — TFF üst sınırı 14. En az ' +
        (listRules.foreign.length - FOREIGN_LIMIT) +
        ' yabancıyı listeden çıkarman ya da göndermen gerekiyor.'
      );
    }
    if (listRules.overBase > listRules.youngForeign.length) {
      return (
        '10 yabancı hakkı doldu; kalan ' +
        listRules.overBase +
        ' yabancının 01.01.2003 ve sonrası doğumlu olması gerekiyor, elinde ' +
        listRules.youngForeign.length +
        ' uygun oyuncu var.'
      );
    }
    // Over capacity is a breach and outranks a mere shortfall, so it is tested first.
    // The shortfall is only mentioned here when it is what shrank the capacity.
    if (listRules.listA.length > listRules.listACapacity) {
      const excess = listRules.listA.length - listRules.listACapacity;
      const reason =
        listRules.listACapacity < LIST_A_LIMIT
          ? ' ama yetiştirilmiş oyuncu kotası eksik olduğu için en fazla ' +
            listRules.listACapacity +
            ' bildirebilirsin'
          : ' — azami ' + LIST_A_LIMIT;
      return (
        'UEFA Liste A\'da ' +
        listRules.listA.length +
        ' oyuncu var' +
        reason +
        '. ' +
        excess +
        ' oyuncuyu listeden çıkarman gerekiyor.'
      );
    }
    if (listRules.homegrownShort > 0 || listRules.clubTrainedShort > 0) {
      return (
        'UEFA Liste A yetiştirilmiş oyuncu kotası eksik — listeyi en fazla ' +
        listRules.listACapacity +
        ' kişiyle bildirebilirsin.'
      );
    }
    if (squadRules.totalWage > wageCap) {
      return (
        'Kadro kurallara uygun ancak maaş yükü bütçeyi ' +
        money(squadRules.totalWage - wageCap) +
        ' aşıyor.'
      );
    }
    return (
      'Kadro TFF ve UEFA kriterlerini karşılıyor. Liste A\'da ' +
      listRules.listA.length +
      ' oyuncu, ' +
      listRules.homegrown.length +
      ' yetiştirilmiş oyuncu bildirildi.'
    );
  }, [listRules, squadRules, wageCap]);

  const cellStyle = {
    padding: '10px 14px',
    borderRight: '1px solid ' + HEADER.divider,
  };
  const labelStyle = {
    fontSize: 9.5,
    letterSpacing: '0.14em',
    color: HEADER.muted,
    fontWeight: 600,
  };
  const valueStyle = { fontSize: 22, fontWeight: 800, lineHeight: 1.15 };
  const noteStyle = {
    fontSize: 9,
    fontWeight: 600,
    color: HEADER.muted,
    marginTop: 2,
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'stretch',
          borderBottom: '2px solid ' + C.ink,
          background: HEADER.bg,
          color: HEADER.fg,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            padding: '14px 20px',
            borderRight: '2px solid ' + HEADER.fg,
          }}
        >
          <div
            style={{
              width: 34,
              height: 34,
              background: C.red,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 800,
              fontSize: 15,
              color: HEADER.fg,
            }}
          >
            GS
          </div>
          <div>
            <div
              style={{
                fontSize: 19,
                fontWeight: 800,
                letterSpacing: '-0.02em',
                lineHeight: 1,
              }}
            >
              GSKADRO<span style={{ color: C.gold }}>.COM</span>
            </div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.16em',
                color: HEADER.muted,
                marginTop: 3,
              }}
            >
              KADRO PLANLAMA TERMİNALİ
            </div>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 5,
            padding: '0 16px',
            borderRight: '2px solid ' + HEADER.divider,
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.1em',
              color: C.gold,
            }}
          >
            SEZON 2026/27
          </span>
          <button
            onClick={resetPlan}
            title="Kaydedilmiş planı sil ve başlangıç kadrosuna dön"
            style={{
              border: '1px solid ' + HEADER.outline,
              background: 'transparent',
              color: HEADER.muted,
              fontSize: 9,
              fontWeight: 800,
              letterSpacing: '0.1em',
              padding: '3px 6px',
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            PLANI SIFIRLA
          </button>
        </div>

        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
          <div style={cellStyle}>
            <div style={labelStyle}>KADRO</div>
            <div style={valueStyle}>{kpi.count}</div>
          </div>
          <div style={cellStyle}>
            <div style={labelStyle}>YAŞ ORT.</div>
            <div style={valueStyle}>{kpi.averageAge}</div>
          </div>
          <div style={cellStyle}>
            <div style={labelStyle}>TOPLAM DEĞER</div>
            <div style={valueStyle}>{kpi.value}</div>
            <div style={noteStyle}>{kpi.valueNote}</div>
          </div>
          <div style={cellStyle}>
            <div style={labelStyle}>MAAŞ / YIL</div>
            <div style={{ ...valueStyle, color: kpi.wageFg }}>{kpi.wage}</div>
            <div style={noteStyle}>{kpi.wageNote}</div>
          </div>
          <div style={cellStyle}>
            <div style={labelStyle}>YABANCI</div>
            <div style={{ ...valueStyle, color: kpi.foreignFg }}>{kpi.foreign}</div>
          </div>
          <div style={cellStyle}>
            <div style={labelStyle}>ORT. REYTİNG</div>
            <div style={valueStyle}>{kpi.rating}</div>
          </div>
          <div style={{ padding: '10px 14px' }}>
            <div style={labelStyle}>UYGUNLUK</div>
            <div style={{ ...valueStyle, color: kpi.complianceFg }}>{kpi.compliance}</div>
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 0,
          background: C.surface,
          borderBottom: '2px solid ' + C.ink,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '7px 16px',
            background: C.red,
            color: C.bg,
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: '0.14em',
            whiteSpace: 'nowrap',
          }}
        >
          DURUM
        </div>
        <div style={{ padding: '7px 16px', fontSize: 12, fontWeight: 600, lineHeight: 1.4 }}>
          {headline}
        </div>
      </div>
    </>
  );
}
